import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import * as R from 'ramda';
import GoogleAuthButton, { SIGNUP_WITH } from 'src/components/atoms/Button/GoogleAuthButton.jsx';

import { appLogin, appGoogleLogin } from 'models/auth';

import useI18n from 'hooks/I18n/useI18n';

import ResponsiveSidePanel, {
    POSITION_RIGHT,
} from 'components/Navigation/ProductIdentity/ResponsiveSidePanel.jsx';
import LoginForm from 'src/components/organisms/LoginForm/LoginForm.jsx';
import Text, { SMALL } from 'src/components/atoms/Text/Text.jsx';
import { LoadingAnimation } from 'components/ui';

const AnonymousProfilePanel = ({ onClose, fullHeight }) => {
    const ordersCollectionById = useSelector(state => state.ordersCollection?.byId || []);
    const methods = useForm();
    const { watch } = methods;
    const [email, password, remember] = watch(['email', 'password', 'remember']);
    const { pathname } = useLocation();
    const { trans } = useI18n();
    const [googleAuthPending, setGoogleAuthPending] = useState(false);

    const getBasketUuidAndDistributionIdFromOrdersCollection = localOrdersCollectionById => {
        const orderCollectionAnonymous = R.find(R.propEq('anonymous', true))(
            R.values(localOrdersCollectionById)
        );
        return {
            anonymousBasketUuid: orderCollectionAnonymous?.uuid,
            distributionId: orderCollectionAnonymous?.distributionId,
        };
    };

    const handleGoogleCredentialResponse = response => {
        const {
            anonymousBasketUuid,
            distributionId,
        } = getBasketUuidAndDistributionIdFromOrdersCollection(ordersCollectionById);

        const onCallbackFail = () => setGoogleAuthPending(false);
        const onCallbackSucceed = () => setGoogleAuthPending(false);
        const jwt = response.credential;
        setGoogleAuthPending(true);
        return appGoogleLogin(jwt, {
            onCallbackSucceed,
            onCallbackFail,
            anonymousBasketUuid,
            distributionId,
            redirectUrl: pathname,
        });
    };

    const handleSubmit = useCallback(() => {
        const {
            anonymousBasketUuid,
            distributionId,
        } = getBasketUuidAndDistributionIdFromOrdersCollection(ordersCollectionById);

        return appLogin(
            email,
            password,
            remember,
            pathname,
            anonymousBasketUuid,
            distributionId,
            true
        );
    }, [email, password, remember, pathname, ordersCollectionById]);

    return (
        <ResponsiveSidePanel
            isOpened
            position={POSITION_RIGHT}
            title={trans('login.title')}
            onClose={onClose}
            className={classNames('pi-anonymous-profile-panel', { fullHeight })}
        >
            <FormProvider {...methods}>
                {process.env.GOOGLE_CLIENT_ID && (
                    <>
                        {googleAuthPending ? (
                            <LoadingAnimation type="pi-spinner" />
                        ) : (
                            <GoogleAuthButton
                                clientId={process.env.GOOGLE_CLIENT_ID}
                                text={SIGNUP_WITH}
                                callback={handleGoogleCredentialResponse}
                            />
                        )}
                        <div className="login-panel-separator">
                            <Text size={SMALL}>{trans('global.or')}</Text>
                        </div>
                    </>
                )}
                <LoginForm onSubmit={handleSubmit} />
            </FormProvider>
        </ResponsiveSidePanel>
    );
};

AnonymousProfilePanel.propTypes = {
    onClose: PropTypes.func.isRequired,
    fullHeight: PropTypes.bool,
};

export default AnonymousProfilePanel;
